export interface StudyReportSectionNavigationInput {
  id: string;
  title: string;
  numberLabel?: string;
  navParentId?: string;
  navParentTitle?: string;
  rowGroupId?: string;
  rowColumns?: number;
}

export interface StudyReportResolvedSectionNavigation extends StudyReportSectionNavigationInput {
  numberLabel: string;
}

export interface StudyReportTocItem {
  key: string;
  title: string;
  numberLabel: string;
  depth: number;
  sectionId?: string;
  parentId?: string;
}

export interface StudyReportSectionRow<Section extends StudyReportSectionNavigationInput> {
  key: string;
  rowGroupId?: string;
  rowColumns: number;
  isFirstRowInGroup: boolean;
  sections: Section[];
}

export interface StudyReportSectionNavigationResult<Section extends StudyReportSectionNavigationInput> {
  resolvedSections: Array<Section & { numberLabel: string }>;
  tocItems: StudyReportTocItem[];
  parentNumberLabelById: Map<string, string>;
  parentTitleById: Map<string, string>;
}

export const buildStudyReportSectionRows = <Section extends StudyReportSectionNavigationInput>(
  sections: Section[]
): StudyReportSectionRow<Section>[] => {
  const rows: StudyReportSectionRow<Section>[] = [];

  for (let index = 0; index < sections.length; ) {
    const currentSection = sections[index];
    const rowColumns = Math.max(currentSection.rowColumns || 1, 1);

    if (!currentSection.rowGroupId) {
      rows.push({
        key: currentSection.id,
        rowColumns: 1,
        isFirstRowInGroup: false,
        sections: [currentSection],
      });
      index += 1;
      continue;
    }

    const groupedSections: Section[] = [];
    const { rowGroupId } = currentSection;

    while (index < sections.length && sections[index].rowGroupId === rowGroupId) {
      groupedSections.push(sections[index]);
      index += 1;
    }

    for (let chunkStart = 0; chunkStart < groupedSections.length; chunkStart += rowColumns) {
      rows.push({
        key: `${rowGroupId}:${chunkStart / rowColumns}`,
        rowGroupId,
        rowColumns,
        isFirstRowInGroup: chunkStart === 0,
        sections: groupedSections.slice(chunkStart, chunkStart + rowColumns),
      });
    }
  }

  return rows;
};

export const buildStudyReportSectionNavigation = <Section extends StudyReportSectionNavigationInput>(
  sections: Section[]
): StudyReportSectionNavigationResult<Section> => {
  let rootIndex = 0;
  const parentNumberLabelById = new Map<string, string>();
  const parentTitleById = new Map<string, string>();
  const childCountByParentId = new Map<string, number>();

  const resolvedSections = sections.map((section) => {
    if (section.numberLabel) {
      return {
        ...section,
        numberLabel: section.numberLabel,
      };
    }

    if (!section.navParentId) {
      rootIndex += 1;

      return {
        ...section,
        numberLabel: `${rootIndex}`,
      };
    }

    if (!parentNumberLabelById.has(section.navParentId)) {
      rootIndex += 1;
      parentNumberLabelById.set(section.navParentId, `${rootIndex}`);
      parentTitleById.set(section.navParentId, section.navParentTitle || section.title);
      childCountByParentId.set(section.navParentId, 0);
    }

    const nextChildIndex = (childCountByParentId.get(section.navParentId) || 0) + 1;
    childCountByParentId.set(section.navParentId, nextChildIndex);

    return {
      ...section,
      numberLabel: `${parentNumberLabelById.get(section.navParentId)}.${nextChildIndex}`,
    };
  });

  const seenParentIds = new Set<string>();
  const tocItems = resolvedSections.flatMap((section) => {
    if (!section.navParentId) {
      return [
        {
          key: section.id,
          title: section.title,
          numberLabel: section.numberLabel,
          depth: 0,
          sectionId: section.id,
        },
      ];
    }

    const items: StudyReportTocItem[] = [];

    if (!seenParentIds.has(section.navParentId)) {
      seenParentIds.add(section.navParentId);
      items.push({
        key: `parent:${section.navParentId}`,
        title: parentTitleById.get(section.navParentId) || section.navParentTitle || section.title,
        numberLabel:
          parentNumberLabelById.get(section.navParentId) || section.numberLabel.split('.').slice(0, -1).join('.'),
        depth: 0,
        parentId: section.navParentId,
      });
    }

    items.push({
      key: section.id,
      title: section.title,
      numberLabel: section.numberLabel,
      depth: 1,
      sectionId: section.id,
      parentId: section.navParentId,
    });

    return items;
  });

  return {
    resolvedSections,
    tocItems,
    parentNumberLabelById,
    parentTitleById,
  };
};
